import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { GetAppointmentsQueryParams } from '../types/appointment.types';

interface Patient {
  id: string;
  fullName: string;
  mrn: string;
  // Add other patient properties as needed
}
import { 
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { FilterList as FilterListIcon, Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import { useAppointments } from '../hooks/useAppointments';
import type { ExtendedAppointmentsQueryParams } from '../hooks/useAppointments';
import { employeeApi } from '../../employees/api/employeeApi';
import { useAppointmentsUI } from '../context/AppointmentsContext';
import AppointmentsView from '../components/AppointmentsView';
import AppointmentDialog from '../components/AppointmentDialog';
import { apiClient } from '../../../api/apiClient';
import { 
  AppointmentStatus, 
} from '../types/appointment.types';
import { 
  type AppointmentStatusType,
  type Appointment,
  type PatientDto,
  type LocationDto,
  type EmployeeDto,
} from '../types/appointment.types';
import { useAuth } from '@/contexts/AuthContext';

// Helper function to get start and end of day - moved outside component for Fast Refresh compatibility
function getStartAndEndOfDay(date: Date = new Date()) {
  const localDate = new Date(date);
  
  // Get local date components
  const year = localDate.getFullYear();
  const month = localDate.getMonth();
  const day = localDate.getDate();
  
  // Create start of day (00:00:00.000)
  const start = new Date(year, month, day, 0, 0, 0, 0);
  
  // Create end of day (23:59:59.999)
  const end = new Date(year, month, day, 23, 59, 59, 999);
  
  return { start, end };
}

// Status labels mapped to their display names - moved outside component for Fast Refresh compatibility
const statusLabels: Record<string, string> = {
  'SCHEDULED': 'Scheduled',
  'CONFIRMED': 'Confirmed',
  'ARRIVED': 'Arrived',
  'IN_PROGRESS': 'In Progress',
  'COMPLETED': 'Completed',
  'CANCELLED': 'Cancelled',
  'NO_SHOW': 'No Show',
  'RESCHEDULED': 'Rescheduled'
} as const;

const AppointmentsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const patientIdFromQuery = searchParams.get('patientId');
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const today = new Date();
  const { start: startOfToday, end: endOfToday } = getStartAndEndOfDay();
  
  // State for UI
  const [isLoading, setIsLoading] = useState(false);
  // Track if we've set patient from query
  const [initializedPatientFromQuery, setInitializedPatientFromQuery] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const isProgrammaticSearch = useRef(false);
  
  // State for dialog
  const [dialogOpen, setDialogOpen] = useState(false);

  // UI: Show selected patient indicator if patientId is present
  const PatientIndicator = () => {
    if (selectedPatient && patientIdFromQuery) {
      return (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            color="primary"
            label={`Showing appointments for: ${selectedPatient.fullName} (MRN: ${selectedPatient.mrn})`}
            sx={{ fontWeight: 'bold', fontSize: 16, px: 2, py: 1 }}
          />
        </Box>
      );
    }
    return null;
  };


  // Fetch patient if patientId is in query and not already selected
  useEffect(() => {
    if (
      patientIdFromQuery &&
      !initializedPatientFromQuery &&
      (!selectedPatient || selectedPatient.id !== patientIdFromQuery)
    ) {
      // Fetch patient info from API
      (async () => {
        try {
          setIsLoading(true);
          const response = await apiClient.get(`/patients/${patientIdFromQuery}`);
          if (response?.data?.data) {
            setSelectedPatient({
              id: response.data.data.id,
              fullName: response.data.data.fullName,
              mrn: response.data.data.mrn,
            });
          }
        } catch (err) {
          // Optionally show error
        } finally {
          setIsLoading(false);
          setInitializedPatientFromQuery(true);
        }
      })();
    }
  }, [patientIdFromQuery, initializedPatientFromQuery, selectedPatient]);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view' | 'delete'>('create');
  const [currentAppointment, setCurrentAppointment] = useState<Appointment | null>(null);
  
  // State for data
  const [patients, setPatients] = useState<PatientDto[]>([]);
  const [doctors, setDoctors] = useState<EmployeeDto[]>([]);
  const [locations, setLocations] = useState<LocationDto[]>([]);
  
  // State for filters
  const [filters, setFilters] = useState<{
    status: AppointmentStatus | '';
    fromDate: Date | null;
    toDate: Date | null;
  }>({
    status: '',
    fromDate: startOfToday, // Set default to today
    toDate: endOfToday     // Set default to today
  });
  
  // Get appointments data
  const { 
    appointments,
    loading,
    error,
    pagination,
    params,
    setParams: setAppointmentParams,
    fetchAppointments: fetchAppointmentsFromHook,
    handlePageChange,
    handleRowsPerPageChange
  } = useAppointments();
  
  // Memoize fetchAppointments to prevent unnecessary re-renders
  const fetchAppointments = useCallback(() => {
    fetchAppointmentsFromHook();
  }, [fetchAppointmentsFromHook]);
  
  // Create a typed version of setParams that handles both direct object and functional updates
  const setParams = useCallback((paramsOrUpdater: Partial<ExtendedAppointmentsQueryParams> | ((prev: ExtendedAppointmentsQueryParams) => Partial<ExtendedAppointmentsQueryParams>)) => {
    const normalizeStatus = (status: unknown): string | string[] | undefined => {
      if (!status) return undefined;
      
      // Get valid status values from our statusLabels
      const validStatuses = Object.keys(statusLabels);
      
      // Convert string to array if it's a comma-separated string
      let statusArray: string[] = [];
      if (typeof status === 'string') {
        statusArray = status.includes(',') ? status.split(',').map(s => s.trim()) : [status];
      } else if (Array.isArray(status)) {
        statusArray = status;
      } else {
        return undefined;
      }
      
      // Filter to only include valid statuses
      const filteredStatuses = statusArray.filter(s => s && validStatuses.includes(s));
      
      // Return as comma-separated string if multiple statuses, single string if one, or undefined if none
      if (filteredStatuses.length === 0) return undefined;
      if (filteredStatuses.length === 1) return filteredStatuses[0];
      return filteredStatuses.join(',');
    };

    if (typeof paramsOrUpdater === 'function') {
      // Handle functional update
      setAppointmentParams((prev: ExtendedAppointmentsQueryParams) => {
        const newParams = paramsOrUpdater(prev);
        // Only normalize status if it exists in the new params
        if ('status' in newParams) {
          return { ...prev, ...newParams, status: normalizeStatus(newParams.status) };
        }
        return { ...prev, ...newParams };
      });
    } else {
      // Handle direct object update
      const newParams = { ...paramsOrUpdater };
      // Only normalize status if it exists in the params
      if ('status' in newParams) {
        newParams.status = normalizeStatus(newParams.status);
      }
      setAppointmentParams((prev: ExtendedAppointmentsQueryParams) => ({
        ...prev,
        ...newParams
      }));
    }
  }, [setAppointmentParams]);
  
  // Set initial date range on mount and fetch appointments
  useEffect(() => {
    console.log('Setting initial date range and fetching appointments...');
    
    // Set initial params - using comma-separated string for multiple statuses as expected by the backend
    setParams(prev => ({
      ...prev,
      startDate: startOfToday.toISOString(),
      endDate: endOfToday.toISOString(),
      status: 'SCHEDULED,RESCHEDULED' // Comma-separated string for multiple statuses
    }));
  }, [setParams]);
  
  // Fetch appointments when params change or on initial load
  useEffect(() => {
    console.log('Fetching appointments with params:', params);
    
    // Only fetch if we have valid date range
    if (params.startDate && params.endDate) {
      fetchAppointments();
    }
  }, [params, fetchAppointments]);
  
  // Log appointments changes
  useEffect(() => {
    console.log('Appointments updated:', appointments);
  }, [appointments]);
  
  // Get userId from localStorage (fallback if not in context)
  const { user } = useAuth();
  const localUserId = typeof window !== 'undefined' ? localStorage.getItem('userId') : undefined;
  const userId = user?.id || localUserId;

  // Filter: if userId exists, show only those appointments for this doctor; else show all
  const filteredAppointments = useMemo(() => {
    // Temporarily disable filtering to show all appointments
    return appointments;
    
    // Original filtering logic (commented out for now)
    // if (userId) {
    //   return appointments.filter(
    //     (appt: any) => appt?.doctor?.user?.id === userId
    //   );
    // }
    // return appointments;
  }, [appointments]);

  // Log for verification
  console.log('[AppointmentsPage] userId:', userId, '| filtered:', filteredAppointments.length, '| total appointments:', appointments.length);
  console.log('First 3 appointments doctor info:', appointments.slice(0,3).map(a => ({ 
    appointmentId: a?.id, 
    doctorId: a?.doctorId,
    doctorUserIdNested: a?.doctor?.user?.id,
    doctorObject: a?.doctor 
  })));
  
  // Handle date filter change
  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    
    // Create a new date object to avoid reference issues
    const selectedDate = new Date(date);
    if (isNaN(selectedDate.getTime())) return;
    
    // Reset time components to ensure consistent comparison
    selectedDate.setHours(0, 0, 0, 0);
    
    // Create date objects for the start and end of the selected day in local time
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);
    
    // Log the date information for debugging
    console.log('Selected date:', {
      input: date,
      parsed: selectedDate,
      localStart: startOfDay.toString(),
      localEnd: endOfDay.toString(),
      timezoneOffset: startOfDay.getTimezoneOffset()
    });
    
    // Update the filters state with the date range in local time
    setFilters(prev => ({
      ...prev,
      fromDate: startOfDay,
      toDate: endOfDay
    }));
  };
  
  const { state, openDialog, closeDialog } = useAppointmentsUI();
  


  // Apply filters whenever they change
  useEffect(() => {
    const updateParams = () => {
      const newParams: Partial<GetAppointmentsQueryParams> = {};

      // If we have a selected patient, use the patientId for filtering
      if (selectedPatient) {
        newParams.patientId = selectedPatient.id;
      } else {
        // If no patient is selected, ensure patientId is not in the params
        const { patientId, ...restParams } = params;
        Object.assign(newParams, restParams);
      }

      // Add status filter if provided
      if (filters.status) {
        // Convert status to array if it's not already
        newParams.status = Array.isArray(filters.status) 
          ? filters.status 
          : [filters.status as AppointmentStatus];
      }

      if (filters.fromDate && filters.toDate) {
        // Create new date objects to avoid reference issues
        const start = new Date(filters.fromDate);
        const end = new Date(filters.toDate);
        
        // Set the time components to ensure we get the full day in local time
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        
        // Create dates in local time
        const localStart = new Date(start);
        const localEnd = new Date(end);
        
        // Convert local dates to UTC by adjusting for timezone offset
        const timezoneOffset = localStart.getTimezoneOffset() * 60000;
        
        // Create UTC dates that match the local date range
        const utcStart = new Date(localStart.getTime() - timezoneOffset);
        const utcEnd = new Date(localEnd.getTime() - timezoneOffset);
        
        // Set the API params with UTC dates
        newParams.startDate = utcStart.toISOString();
        newParams.endDate = utcEnd.toISOString();
        
        console.log('Date range for API:', { 
          localStart: start.toString(),
          localEnd: end.toString(),
          utcStart: newParams.startDate,
          utcEnd: newParams.endDate,
          timezoneOffset: start.getTimezoneOffset()
        });
      } else {
        // Clear date filters if not set
        delete newParams.startDate;
        delete newParams.endDate;
      }
      
      // Don't reset page when filters change, only update the filter params
      const cleanParams: Record<string, any> = {
        ...newParams,
        limit: pagination.limit,
        // Only include page if we have pagination data
        ...(pagination.total > 0 && { page: pagination.page + 1 }) // API is 1-based, our UI is 0-based
      };
      
      console.log('Updating params with:', cleanParams);
      
      // Only update if the params have actually changed
      const currentParams = JSON.stringify(params);
      const newParamsStr = JSON.stringify(cleanParams);
      
      if (currentParams !== newParamsStr) {
        setParams(cleanParams as GetAppointmentsQueryParams);
      }
    };
    
    updateParams();
    // Only run when filters or searchTerm changes, not when setParams or pagination changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedPatient, setParams, params]);
  
  // Using handlePageChange and handleRowsPerPageChange from useAppointments hook

  // Search for patients by name
  const searchPatients = async (query: string) => {
    if (!query.trim()) {
      setPatientSearchResults([]);
      return;
    }
    
    try {
      const response = await apiClient.get('/patients', {
        params: {
          search: query,
          limit: 5,
          fields: 'id,fullName,mrn' // Only fetch necessary fields
        }
      });
      setPatientSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Error searching patients:', error);
      setPatientSearchResults([]);
      enqueueSnackbar('Failed to search patients', { variant: 'error' });
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Only clear selectedPatient if the search term doesn't match the selected patient's name
    if (selectedPatient && value !== selectedPatient.fullName) {
      setSelectedPatient(null);
    }

    // Only search if not a programmatic change
    if (!isProgrammaticSearch.current) {
      searchPatients(value);
    } else {
      isProgrammaticSearch.current = false;
    }
  };

  // Handle patient selection from search results
  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    isProgrammaticSearch.current = true;
    setSearchTerm(`${patient.fullName}`);
    setPatientSearchResults([]);
    
    // Update params with the selected patient ID
    setParams(prev => ({
      ...prev,
      patientId: patient.id,
      page: 1 // Reset to first page
    }));
    
    // Close the search results dropdown
    const input = document.activeElement as HTMLElement;
    if (input) {
      input.blur();
    }
  };
  


  // Handle individual filter changes
  const handleFilterChange = <K extends keyof typeof filters>(
    field: K, 
    value: typeof filters[K]
  ) => {
    // Update the local filter state
    setFilters(prev => ({ ...prev, [field]: value }));
    
    // If this is a status filter change, update the API params
    if (field === 'status') {
      const validStatuses = Object.keys(statusLabels);
      let statusParam: string | undefined;
      
      if (value === '') {
        // If 'All Status' is selected, remove the status filter
        statusParam = undefined;
      } else if (validStatuses.includes(value as string)) {
        // Only set the status if it's a valid status
        statusParam = value as string;
      }
      
      // Update the API params with the new status filter
      setParams(prev => ({
        ...prev,
        page: 1, // Reset to first page when changing filters
        status: statusParam
      }));
    }
  };

  // Reset all filters and search
  const handleClearFilters = useCallback(() => {
    // Reset all filters to their default values
    const { start, end } = getStartAndEndOfDay();
    
    // Reset local filter state
    setFilters({
      status: '',
      fromDate: start,
      toDate: end
    });
    
    // Reset search state
    setSearchTerm('');
    setSelectedPatient(null);
    setPatientSearchResults([]);
    
    // Reset API params to default
    setParams((prev: GetAppointmentsQueryParams) => ({
      ...prev,
      page: 1,
      limit: 10,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      patientId: undefined, // Explicitly remove patientId
      status: undefined     // Explicitly remove status
    }));
  }, [setParams]);

  // Handle view appointment details
  const handleViewAppointment = (appointment: any) => {
    console.log('View appointment:', appointment);
    // Here you can open a modal or navigate to appointment details
  };
  
  // Clear search term and reset patient selection
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setSelectedPatient(null);
    setPatientSearchResults([]);
    
    // Remove patientId from params
    setParams((prev: GetAppointmentsQueryParams) => {
      const { patientId, ...restParams } = prev;
      return {
        ...restParams,
        page: 1 // Reset to first page
      };
    });
  }, [setParams]);

  const handleSaveAppointment = async (appointmentData: Partial<Appointment>): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Format the data for the API
      const formattedAppointment = {
        ...appointmentData,
        // Ensure required fields are present
        patientId: appointmentData.patientId,
        doctorId: appointmentData.doctorId,
        locationId: appointmentData.locationId,
        startTime: appointmentData.startTime,
        endTime: appointmentData.endTime,
        status: appointmentData.status || 'SCHEDULED',
        type: appointmentData.type || 'GENERAL',
        priority: appointmentData.priority || 'MEDIUM',
        isWalkIn: appointmentData.isWalkIn || false
      };

      console.log('Saving appointment:', formattedAppointment);
      
      // Make API call to save the appointment
      const response = await (dialogMode === 'create' 
        ? apiClient.post('/appointments', formattedAppointment)
        : apiClient.put(`/appointments/${appointmentData.id}`, formattedAppointment));
      
      console.log('Appointment saved successfully:', response.data);
      
      // Show success message with more details
      const action = dialogMode === 'create' ? 'created' : 'updated';
      enqueueSnackbar(
        `✅ Appointment ${action} successfully!`,
        { 
          variant: 'success',
          autoHideDuration: 3000, // Show for 3 seconds
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          // Add a close button
          action: (key) => (
            <IconButton 
              size="small" 
              color="inherit" 
              onClick={() => closeSnackbar(key)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        }
      );
      
      // Only refresh if appointment is for today and startTime is defined
      if (formattedAppointment.startTime) {
        const appointmentDate = new Date(formattedAppointment.startTime);
        const now = new Date();
        const isToday = appointmentDate.getFullYear() === now.getFullYear() &&
          appointmentDate.getMonth() === now.getMonth() &&
          appointmentDate.getDate() === now.getDate();

        if (isToday) {
          const { start, end } = getStartAndEndOfDay(now);
          setParams({
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            page: 1
          });
        }
      }

      // Close the dialog
      closeDialog();
      
    } catch (error: any) {
      console.error('Error saving appointment:', error);
      
      // Extract the error message from the response
      const errorResponse = error.response?.data || {};
      const errorMessage = errorResponse.message || 'Failed to save appointment';
      
      // Log the complete error response for debugging
      console.log('Error response:', error.response);
      
      // Re-throw the error to be caught by the dialog
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAppointment = async (id: string) => {
    try {
      setIsLoading(true);
      // TODO: Implement delete logic here
      console.log('Deleting appointment with id:', id);
      // After delete, close the dialog and refresh the appointments
      closeDialog();
    } catch (error) {
      console.error('Error deleting appointment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch patients, doctors, and locations
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        console.log('Starting to fetch data...');
        
        // Log the current state before fetching
        console.log('Current doctors state before fetch:', doctors);
        
        // Fetch all data in parallel
        console.log('Fetching data from API...');
        const [patientsResponse, doctorsResponse, locationsResponse] = await Promise.all([
          // Fetch patients
          apiClient.get('/patients', {
            params: {
              page: 1,
              limit: 100,
              status: 'active'
            }
          }),
          
          // Fetch doctors using the employee API service
          employeeApi.getDoctors(),
          
          // Fetch locations
          apiClient.get('/locations', {
            params: {
              status: 'active',
              limit: 100
            }
          })
        ]);
        
        // Log the raw responses for debugging
        console.log('Patients API Response:', patientsResponse?.data);
        console.log('Doctors API Response:', doctorsResponse);
        console.log('Locations API Response:', locationsResponse?.data);
        
        // Process doctors data - employeeApi.getDoctors() returns the array directly
        const doctorsData = Array.isArray(doctorsResponse) ? doctorsResponse : [];
        console.log('Processed doctors data:', doctorsData);
        
        // Map to EmployeeDto format
        const formattedDoctors = doctorsData.map(doctor => ({
          id: doctor.id,
          employeeId: doctor.id, // Using id as employeeId since it's required
          firstName: doctor.firstName || '',
          lastName: doctor.lastName || '',
          fullName: doctor.firstName && doctor.lastName 
            ? `${doctor.firstName} ${doctor.lastName}` 
            : 'Unknown Doctor',
          email: doctor.email,
          phone: doctor.phoneNumber,
          employeeType: doctor.employeeType || doctor.employeeType || 'DOCTOR',
          status: doctor.isActive ? 'ACTIVE' : 'INACTIVE',
          // Add any other required fields with defaults
          department: doctor.departmentId ? { 
            id: doctor.departmentId, 
            name: '' 
          } : undefined
        }));
        
        console.log('Formatted doctors data:', formattedDoctors);
        
        // Update state with API data
        setPatients(patientsResponse?.data?.data || []);
        setDoctors(formattedDoctors);
        setLocations(locationsResponse?.data?.data || []);
        
        // Log the state after setting
        console.log('State after setting doctors:', { 
          doctorsCount: formattedDoctors.length,
          firstDoctor: formattedDoctors[0]
        });
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h1">Appointments</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DatePicker
                label="Select Date"
                value={filters.fromDate}
                onChange={handleDateChange}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    onKeyDown: (e) => e.preventDefault(),
                    inputProps: {
                      placeholder: 'DD/MM/YYYY',
                    },
                  },
                }}
                disableFuture={false}
                openTo="day"
                views={['year', 'month', 'day']}
              />
            </LocalizationProvider>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => openDialog('create')}
            sx={{ minWidth: '180px' }}
          >
            New Appointment
          </Button>
        </Box>
        
        {/* Search and Filter Bar */}
        <Paper sx={{ mb: 3, p: 2 }}>
          <Box display="flex" gap={2} alignItems="center">
            
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Filters'}
            </Button>
          </Box>

          {/* Advanced Filters */}
          {showFilters && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value as AppointmentStatus | '')}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <DatePicker
                      label="From Date"
                      value={filters.fromDate}
                      onChange={(date) => handleFilterChange('fromDate', date)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <DatePicker
                      label="To Date"
                      value={filters.toDate}
                      onChange={(date) => handleFilterChange('toDate', date)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    disabled={!filters.status && !filters.fromDate && !filters.toDate && !searchTerm}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {/* Appointments List */}
        <AppointmentsView
          appointments={appointments}
          loading={loading}
          error={error}
          pagination={{
            page: Math.max(0, pagination.page - 1), // Ensure page is not negative
            limit: pagination.limit,
            total: pagination.total,
            totalPages: Math.ceil(pagination.total / pagination.limit),
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
          }}
          onViewAppointment={handleViewAppointment}
          onStatusUpdate={(updatedAppointment) => {
            // Refresh the appointments list to reflect the status change
            console.log('Appointment status updated, refreshing list...', updatedAppointment);
            fetchAppointments();
          }}
        />

        {/* Appointment Dialog */}
        <AppointmentDialog
          open={state.isDialogOpen}
          onClose={closeDialog}
          appointment={currentAppointment || undefined}
          mode={state.dialogType}
          onSave={handleSaveAppointment}
          onDelete={handleDeleteAppointment}
          onRefresh={fetchAppointments}
          patients={patients}
          doctors={doctors}
          locations={locations}
          loading={isLoading}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AppointmentsPage;